// Platform-agnostic formatting helpers (ru-RU locale).
import { DATE_RE } from './date'
import type { Lang } from '../i18n/strings'

export function fmtDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
}

export function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const min = Math.floor(diff / 60000)
  if (min < 1) return 'только что'
  if (min < 60) return `${min} ${pluralRu(min, 'минуту', 'минуты', 'минут')} назад`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h} ${pluralRu(h, 'час', 'часа', 'часов')} назад`
  const d = Math.floor(h / 24)
  if (d < 30) return `${d} ${pluralRu(d, 'день', 'дня', 'дней')} назад`
  return fmtDate(iso)
}

/** «Gürcüstan» ≈ «gurc»: без регистра и диакритики; у ə и ı нет NFD-разложения — заменяем явно. */
export const fold = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/ə/g, 'e').replace(/ı/g, 'i')

/** Russian plural selector. */
export function pluralRu(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few
  return many
}

/** Inclusive day count between two ISO dates. */
export function daysInclusive(startIso: string, endIso: string): number {
  const a = new Date(startIso).getTime()
  const b = new Date(endIso).getTime()
  return Math.round((b - a) / 86400000) + 1
}

/** Число без хвостовых нулей: 8 → «8», 7.5 → «7.5». Не-число → «—». */
export function fmtNum(n: number, digits = 1): string {
  if (!Number.isFinite(n)) return '—'
  return Number.isInteger(n) ? String(n) : n.toFixed(digits)
}

/** Размер файла в КБ, не меньше 1: «0 КБ» у непустого файла выглядит как ошибка. */
export const fmtKb = (size: number) => `${Math.max(1, Math.round(size / 1024))} КБ`

/** ISO-дата/таймстамп → `YYYY-MM-DD` (пусто, если значения нет). */
export const ymd10 = (v?: string | null): string => (v ? String(v).slice(0, 10) : '')

/**
 * Совпадает ли месяц/день дня рождения с today. Парсим строку без `new Date(iso)`,
 * чтобы не ловить таймзонные сдвиги (UTC-полночь могла бы съехать на соседний день).
 */
export const isBirthdayToday = (iso?: string | null, today: Date = new Date()): boolean => {
  if (!iso) return false
  const s = iso.slice(0, 10)
  if (!DATE_RE.test(s)) return false
  const month = Number(s.slice(5, 7))
  const day = Number(s.slice(8, 10))
  // ponytail: 29 Feb → показываем только в високосный год; праздновать 28-го — если попросят
  return month === today.getMonth() + 1 && day === today.getDate()
}

/**
 * Названия месяцев в родительном падеже, своей таблицей, а не через `Intl`: на нативе Hermes
 * собран без полных локалей, и `toLocaleDateString('az')` там отдаёт латиницей по-английски.
 * Падеж нужен именно родительный: «10 ноября», а не «10 ноябрь».
 */
const MONTHS: Record<Lang, readonly string[]> = {
  ru: ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  az: ['yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun', 'iyul', 'avqust', 'sentyabr', 'oktyabr', 'noyabr', 'dekabr'],
  ka: ['იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი', 'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი'],
}

/** Части `YYYY-MM-DD` без `new Date`: UTC-полночь иначе съезжает на соседний день. */
const parts = (iso?: string | null) => {
  const s = ymd10(iso)
  if (!DATE_RE.test(s)) return null
  return { y: s.slice(0, 4), m: Number(s.slice(5, 7)), d: Number(s.slice(8, 10)) }
}

/** `2026-11-10` → `10.11.2026`; пусто, если даты нет. */
export const fmtDmy = (iso?: string | null): string => {
  const p = parts(iso)
  return p ? `${String(p.d).padStart(2, '0')}.${String(p.m).padStart(2, '0')}.${p.y}` : ''
}

/** Одна дата словами: «10 ноября 2026». */
export function fmtDateLong(iso: string | null | undefined, lang: Lang): string {
  const p = parts(iso)
  return p ? `${p.d} ${MONTHS[lang][p.m - 1]} ${p.y}` : ''
}

/**
 * Диапазон поездки одной строкой. Месяц и год не повторяем, пока они совпадают: «10–12 ноября
 * 2026», на переходе месяца «28 ноября — 2 декабря 2026», года — «28 декабря 2026 — 2 января 2027».
 * Одна известная дата печатается как одна дата, а не как диапазон в никуда.
 */
export function fmtDateRange(
  fromIso: string | null | undefined,
  toIso: string | null | undefined,
  lang: Lang,
): string {
  const a = parts(fromIso)
  const b = parts(toIso)
  // Одна из дат не распарсилась — печатаем ту, что распарсилась: пустая строка хуже половины.
  if (!a || !b) return fmtDateLong(a ? fromIso : toIso, lang)
  const months = MONTHS[lang]
  if (a.y === b.y && a.m === b.m) return `${a.d}–${b.d} ${months[a.m - 1]} ${a.y}`
  if (a.y === b.y) return `${a.d} ${months[a.m - 1]} — ${b.d} ${months[b.m - 1]} ${a.y}`
  return `${a.d} ${months[a.m - 1]} ${a.y} — ${b.d} ${months[b.m - 1]} ${b.y}`
}

/** Ночей в поездке: дни включительно минус один. Неполные данные — null, а не ноль. */
export function nightsBetween(
  fromIso?: string | null,
  toIso?: string | null,
): number | null {
  if (!parts(fromIso) || !parts(toIso)) return null
  const n = daysInclusive(ymd10(fromIso), ymd10(toIso)) - 1
  return n >= 0 ? n : null
}

/**
 * Сокращения месяцев для плитки вылета — тоже таблицей: обрезать полное название по буквам
 * нельзя, «мая» → «мая», а «сентября» → «сент», и в каждом языке правило своё.
 */
const MONTHS_SHORT: Record<Lang, readonly string[]> = {
  ru: ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  az: ['yan', 'fev', 'mar', 'apr', 'may', 'iyn', 'iyl', 'avq', 'sen', 'okt', 'noy', 'dek'],
  ka: ['იან', 'თებ', 'მარ', 'აპრ', 'მაი', 'ივნ', 'ივლ', 'აგვ', 'სექ', 'ოქტ', 'ნოე', 'დეკ'],
}

/** День и короткий месяц для плитки вылета: «10» / «ноя». Нет даты — null, плитку не рисуем. */
export function fmtDayTile(
  iso: string | null | undefined,
  lang: Lang,
): { day: string; month: string } | null {
  const p = parts(iso)
  return p ? { day: String(p.d), month: MONTHS_SHORT[lang][p.m - 1] } : null
}
